/* eslint no-use-before-define : 0 */
import { MeRepository, AppIntegrationsRepository } from 'common/repositories/index';
import { Session, SessionKeys } from 'common/services/-RFC.session.js';
import Cookie from 'common/services/util/-RFC.Cookie';
import Alert from 'directives/dashboard/ui-alert/-RFC.ui-alert-logic';
import Mixpanel from 'common/services/stats/-RFC.Mixpanel';
import Recaptcha from 'services/recaptcha';
import socket from 'common/services/webSockets';
import * as Sentry from '@sentry/browser';

/**
 * @ngdoc service
 * @name services.Auth
 *
 * @description
 * Auth service for making login, signup, logout, forgout & reset backend calls and
 * checking if a user is logged in or not (using data from cookies).
 *
 * <div class="alert alert-success">
 * **Best Practice:** It's recommended to use the Auth service for all auth events (i.e:
 * login, reset, forgot, sign up, log out) and for performing the initial token check.
 * </div>
 */

angular.module('services')
    .service('Auth', (
        $rootScope, $window, $state, $location, $timeout, Util, UserRestService,
        OAuthRestService, PasswordsRestService, ErrorHandler,
        LogoutRestService, JWT,
    ) => {


        const cookieConfig = CWCONFIG.cookie;
        const { domain, rootDomain } = cookieConfig;

        const Auth = {

            /**
             * @ngdoc property
             * @name services.Auth#request
             * @propertyOf services.Auth
             *
             * @description
             * If no login request has been made, its value is false,
             * otherwise will be set to true.
             */
            loading: false,
            // keep track of info when trying to login with a social account
            socialLogin: {
                socialLoginButton: '', // button type (facebook/google)
                loginAttempt: false,
            },
            error: false,
            errorReason: '',
            failedLogins: 0,
            captchaRequired: false,
            rememberTfa: false,

            /**
             * @ngdoc function
             * @name services.Auth#login
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. Send a http request to server with user's payload & header and on success
             * we set the login cookies and prepare the user session.
             * @param {Object} payload Payload for the http request.
             * (grant_type: 'password', email: '', password: '')
             * @param {Object} header Payload for the http request.
             * ('X-Avira-Otp': '', 'X-Avira-Browser': '',
                'X-Avira-Browser-Trusted': '', 'X-Avira-Os': '')
             */
            login(payload, header, page, provider) {

                const deviceToken = this.getDeviceToken();
                if (deviceToken) {

                    payload.device_token = deviceToken;

                }

                // Send token from session storage for launcher forgot flow
                const connectClientToken = localStorage.getItem('ConnectClientToken');
                const connectClientDeviceID = localStorage.getItem('ConnectClientDeviceID');
                const resetEmailDeviceID = localStorage.getItem('ResetEmailDeviceID');

                if (connectClientToken && connectClientDeviceID === resetEmailDeviceID) {

                    header.Authorization = `Bearer ${connectClientToken}`;

                }

                OAuthRestService
                    .post(payload, header)
                    .then((response) => {

                        /* page param is passed when the user
                        authetication is done via Google, Facebook or Apple */
                        const pageName = page || 'Login';

                        /* if a user is coming from apple sign in don't log out
                           the user is he has 2fa active */
                        if (Auth.socialLogin.socialLoginButton === 'apple') {

                            sessionStorage.setItem('TfaSessionStorage', 'apple_auth');

                        }

                        Auth.error = false;
                        Recaptcha.disableCaptcha();
                        Auth.failedLogins = 0;
                        Session.setValue(SessionKeys.TokenExpiration, response.data.expires_in);
                        Auth.setLoginCookies(response);
                        Auth.setDeviceToken(response);
                        Auth.startLogoutInterval(response.data.expires_in);
                        Auth.getMeAsync();
                        Auth.sendAcpPing();

                        Mixpanel.send(`ConnectWeb ${pageName} Success`, {
                            Method: Util.deepFind(response, 'config.data.grant_type'),
                        });

                        // reset socialLogin info
                        setTimeout(() => {

                            Auth.socialLogin.socialLoginButton = '';
                            Auth.socialLogin.loginAttempt = false;

                        }, 10000);

                        localStorage.removeItem('ConnectClientToken');
                        localStorage.removeItem('ConnectClientDeviceID');
                        localStorage.removeItem('ResetEmailDeviceID');

                    })
                    .then(null, (error) => {

                        const responseError = Util.deepFind(error, 'data.error');
                        let phoneNumber;

                        // if the user doesn't try to use tfa, it means we have a genuine error
                        if (responseError !== 'invalid_otp') {

                            Mixpanel.send('ConnectWeb Login Error', {
                                'Error Name': responseError,
                                'Error Code': error.status,
                            });

                        }
                        Auth.loading = false;

                        // reset socialLogin info
                        Auth.socialLogin.socialLoginButton = '';
                        Auth.socialLogin.loginAttempt = false;
                        Auth.failedLogins += 1;

                        switch (responseError) {

                        case 'invalid_credentials':

                            Auth.error = true;

                            Util.triggerAnimation('#error-message-form', 'animated shake');

                            // check error header for captcha required
                            if (error.headers('X-Avira-Captcha') === 'required') {

                                Recaptcha.enableCaptcha();

                            }

                            break;

                        case 'required_captcha':

                            Recaptcha.execute();

                            break;

                        case 'invalid_otp':

                            /* save login payload and redirect to TFA Page */
                            $rootScope.userCredentials = {
                                email: Util.deepFind(error, 'config.data.username'),
                                password: Util.deepFind(error, 'config.data.password'),
                                grant_type: Util.deepFind(error, 'config.data.grant_type'),
                                partner: Util.deepFind(error, 'config.data.partner'),
                                token: Util.deepFind(error, 'config.data.token'),
                            };

                            phoneNumber = error.headers()['x-avira-phone'];
                            Auth.sendPhoneNumberAlertSuccess(phoneNumber);

                            Auth.gotoTfa();
                            sessionStorage.setItem('TfaSessionStorage', 'invalid_otp');

                            break;

                        case 'retries_exceeded':

                            Alert.error('cw.auth.tfa.error.retries_exceeded');
                            break;

                        case 'disabled_account':
                            Alert.error('cw.auth.account.disabled');
                            break;

                        case 'no_external_email':
                            Alert.error({
                                message: 'cw.error.no_external_email',
                            }, {
                                message: {
                                    provider,
                                },
                            });
                            break;

                        default:

                            Alert.error('cw.notification.system.error');

                        }

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#sendPhoneNumberAlertSuccess
             * @methodOf services.Auth
             *
             * @description
             * Notify the user that a code was sent to his phone number (ex: ******214)
             */
            sendPhoneNumberAlertSuccess(phoneNumber) {

                Alert.success(
                    { message: 'cw.notification.tfa.phoneNumber' },
                    { message: { phoneNumber } },
                );

            },

            /**
             * @ngdoc function
             * @name services.Auth#tfaLogin
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. Send a http request to server with user's payload & header and on success
             * Log in the user with a 2fa code from mobile device
             * @param {Object} payload Payload for the http request.
             * (grant_type: 'password', email: '', password: '')
             * @param {Object} header Payload for the http request.
             * ('X-Avira-Otp': '', 'X-Avira-Browser': '',
                'X-Avira-Browser-Trusted': '', 'X-Avira-Os': '')
             */
            tfaLogin(payload, header, isResendRequest) {

                const deviceToken = this.getDeviceToken();
                if (deviceToken) {

                    payload.device_token = deviceToken;

                }

                OAuthRestService
                    .post(payload, header)
                    .then((response) => {

                        let responseTfaToken;

                        Auth.error = false;
                        Auth.captchaRequired = false;
                        Session.setValue(SessionKeys.TokenExpiration, response.data.expires_in);
                        Auth.setLoginCookies(response);
                        Auth.setDeviceToken(response);
                        Auth.startLogoutInterval(response.data.expires_in);
                        Auth.getMeAsync();
                        Auth.sendAcpPing();

                        Mixpanel.send('ConnectWeb Login Success', {
                            Method: Util.deepFind(response, 'config.data.grant_type'),
                        });

                        if (Auth.rememberTfa) {

                            responseTfaToken = response.headers()['x-avira-token'];

                            Auth.setTfaCookie(responseTfaToken);

                        }

                        // $rootScope userCredentials cleanup
                        delete $rootScope.userCredentials;

                    })
                    .then(null, (error) => {

                        const responseError = Util.deepFind(error, 'data.error');
                        let phoneNumber;

                        Auth.loading = false;

                        Mixpanel.send('ConnectWeb Login Error', {
                            'Error Name': responseError,
                            'Error Code': error.status,
                        });


                        switch (responseError) {

                        case 'expired_otp':

                            Alert.error('cw.auth.tfa.error.expired_otp');
                            break;

                        case 'retries_exceeded':

                            Alert.error('cw.auth.tfa.error.retries_exceeded');
                            break;

                        case 'invalid_otp':

                            // alert success on resending 2FA code
                            if (isResendRequest) {

                                phoneNumber = error.headers()['x-avira-phone'];
                                Auth.sendPhoneNumberAlertSuccess(phoneNumber);

                                Auth.error = false;

                            } else {

                                Auth.error = true;
                                Util.triggerAnimation('.error', 'animated shake');

                            }

                            break;

                        case 'disabled_account':
                            Alert.error('cw.auth.account.disabled');
                            break;

                        default:

                            Alert.error('cw.notification.system.error');
                            ErrorHandler.handleError(error, '2FA OAUTH');

                        }

                    });

            },
            /**
             * @ngdoc function
             * @name services.Auth#loginWithToken
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. Log in the user with existing token (example: token from launcher)
             * @param {String} token This parameter is used to authenticate the user
             */
            loginWithToken(token) {

                // Expected format to use token
                const data = {
                    data: {
                        access_token: token,
                    },
                };

                Auth.removeLoginCookies();
                Auth.setLoginCookies(data);
                Auth.getMeAsync();

            },

            /**
             * @ngdoc function
             * @name services.Auth#register
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. We make a http request in order to obtain a temporary token and
             *    set a cookie with token value. - OAuthRestService.post
             * 2. We create a new user with the information taken from register page.
             *    (userPayload) -
             *    UserRestService.crate(userPayload) - fullname, email, passowrd
             * @param {Object} oauthPayload Payload for the http request.
             * (grant_type: 'client_credentials')
             * @param {Object} userPayload  Payload with user information.
             * (fullname, email, password)
             */
            register(oauthPayload, headers, userPayload) {

                OAuthRestService
                    .post(oauthPayload, headers)
                    .then((oauthResponse) => {


                        Auth.setLoginCookies(oauthResponse);
                        Auth.createUser(userPayload, headers);

                    })
                    .then(null, (error) => {

                        const responseError = Util.deepFind(error, 'data.error');

                        if (responseError === 'required_captcha') {

                            Recaptcha.enableCaptcha();

                        }

                        Auth.loading = false;
                        Alert.error('cw.notification.system.error');

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#registerAnon
             * @methodOf services.Auth
             * @function
             *
             * @description
             * This method is used to create a user when we already
             * have a token stored in cookie
             */
            registerAnon(userPayload) {

                Auth.createUser(userPayload);

            },

            /**
             * @ngdoc function
             * @name services.Auth#createUser
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. We make a http request in order to create a new user.
             * 2. On success we log the user into Avira Connect (Launcher/MYA/Backend)
             * and we redirect him to confirmation page.
             * @param {Object} userPayload  Payload with user information.
             * (fullname, email, password)
             */
            createUser(userPayload, headers) {


                UserRestService
                    .create(userPayload, headers)
                    .then((response) => {

                        $rootScope.newRegisteredUser = true;

                        MeRepository.replace(response.data);

                        Auth.logIntoAviraConnect(userPayload, headers);

                        Auth.error = false;

                    })
                    .then(null, (error) => {

                        const errorObj = Util.deepFind(error, 'data.errors')[0];
                        const errorText = Util.deepFind(errorObj, 'detail');
                        const errorCode = Util.deepFind(errorObj, 'code');
                        const token = Cookie.get(CWCONFIG.cookie.token);

                        Auth.error = false;
                        Auth.failedLogins += 1;

                        Mixpanel.send('ConnectWeb Register Error', {
                            'Error Name': errorText,
                            'Error Code': errorCode,
                        });

                        /* if the user has temporary token and receives an error
                            the temporary token will be deleted in order to
                            let the user make other OAUTH calls. */
                        if (token) {

                            const tokenValues = JWT.getTokenPayload(token);

                            const isTemporaryToken = tokenValues.tid;
                            const tokenScope = tokenValues.scope;

                            if (isTemporaryToken &&
                                tokenScope &&
                                tokenScope.indexOf('me:GET,users:POST,devices:POST') > -1) {

                                Auth.removeLoginCookies();

                            }

                        }

                        __triggerFormErrors(error);

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#forgotPassword
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. We make a http request (POST) with the user email, language and partner.
             *    PasswordsRestService.forgotPassword(payload)
             * 2. The user will receive an email with the reset password link.
             * @param {Object} payload Payload for the http request. (email, partner, language)
             */
            forgotPassword(payload, headers) {

                PasswordsRestService
                    .post(payload, headers)
                    .then(() => {

                        Auth.loading = false;
                        $state.go('root.auth.login');
                        Alert.success('cw.auth.forgot.request_success2');

                    })
                    .then(null, (err) => {

                        const errors = Util.deepFind(err, 'data.errors');

                        if (errors && errors.length && errors[0].status === '429') {

                            Auth.loading = false;
                            Alert.error('cw.auth.forgot.tries_exceeded');


                        } else {

                            Auth.loading = false;
                            Alert.error('cw.auth.forgot.request_error');

                        }

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#resetPassword
             * @methodOf services.Auth
             * @function
             *
             * @description
             * 1. We set a cookie with the temporary token value in order to make the http request.
             * 2. We make a http request (PUT) and update user's password.
             *    PasswordsRestService.update(payload)
             * 3. On success we delete the temporary token
             * @param {Object} payload Payload for the http request. (password)
             */
            resetPassword(payload, token) {

                // set temporary token in order to make http calls
                Cookie.set(cookieConfig.token, token, {
                    expires: 30,
                    path: '/',
                    domain: rootDomain || domain,
                });

                PasswordsRestService
                    .update(payload)
                    .then(() => {

                        // remove temporary token
                        Auth.removeLoginCookies();

                        Auth.loading = false;
                        Auth.error = false;
                        $state.go('root.auth.login');
                        Alert.success('cw.auth.password_changed');

                        Mixpanel.send('ConnectWeb Reset Password Success', {
                            Logout: payload.meta.logout ? 'true' : 'false',
                        });

                    })
                    .then(null, (error) => {

                        Auth.loading = false;
                        Auth.error = true;
                        Auth.errorReason = error.data.error;

                        if (error.data.error !== 'expired_token') {

                            __triggerFormErrors(error);

                        }

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#sendAcpPing
             * @methodOf services.Auth
             * @function
             *
             * @description
             * This command is trying to establish a connection to our websocket server
             * in order to receive ACP notifications.
             */
            sendAcpPing(tokenPayload) {

                const token = tokenPayload || Cookie.get(cookieConfig.token);

                if (token) {

                    if (!socket.isConnected) {

                        socket.connect();

                    }

                }

            },

            /**
             * @ngdoc function
             * @name services.Auth#getMeAsync
             * @methodOf services.Auth
             * @function
             *
             * @description
             * This method gets the current logged user
             * and on success we update the MeRepository.
             */
            getMeAsync() {

                let errorReason;

                UserRestService
                    .getMeAsync()
                    .then((response) => {

                        MeRepository.replace(response.data);

                    }).then(null, (error) => {

                        const token = Cookie.get(CWCONFIG.cookie.token);
                        let tokenSource = 'none';
                        let tokenClient = 'none';

                        if (token) {

                            tokenSource = JWT.getTokenPayload(token).src || 'none';
                            tokenClient = JWT.getTokenPayload(token).cid || 'none';

                        }

                        // apply retry handler only for 0 and 504 statuses
                        if (error.status === 0 || error.status === 504) {

                            if (Util.deepFind(error, 'data.error')) {

                                errorReason = error.data.error;

                            } else if (Util.deepFind(error, 'data.errors')) {

                                errorReason = error.data.errors[0].detail;

                            }

                            Mixpanel.send('ConnectWeb ErrorPage Open', {
                                'Error Name': error.statusText,
                                'Error Code': error.status.toString(),
                                'API Resource': Util.getRestResourceFromUrl(error.config.url),
                                'Error Reason': errorReason,
                                'Error Object Data': error.data,
                                'Token Source': tokenSource,
                                'Token Client': tokenClient,
                            });

                            /* Remove cookies and redirect to login
                            (possible reasons: bad token or request timeout) */
                            Auth.logOut();

                        } else if (error.status === 401 &&
                                   error.data &&
                                   error.data.errors &&
                                   error.data.errors.length &&
                                   error.data.errors[0].code === '910') {

                            Mixpanel.send('ConnectWeb ErrorPage Open', {
                                'Error Name': error.statusText,
                                'Error Code': error.status.toString(),
                                'API Resource': Util.getRestResourceFromUrl(error.config.url),
                                'Error Reason': errorReason,
                                'Error Object Data': error.data,
                                'Token Source': tokenSource,
                                'Token Client': tokenClient,
                            });

                            // If the user is deleted we receive 910 error code
                            Auth.removeLoginCookies();

                            Alert.error(
                                { message: 'cw.auth.error.account_deleted' },
                                {
                                    message: {
                                        brandname: CWCONFIG.partnerConfig.name,
                                        product1: AppIntegrationsRepository.getAppName('avpa0'),
                                        product2: AppIntegrationsRepository.getAppName('pasm0'),
                                    },
                                },
                            );

                            Auth.gotoRegister();

                            // wait for location path to be changed firefox/safari bug
                            $timeout(() => {

                                $window.location.reload();

                            }, CWCONFIG.timeouts.notification);

                        } else if (
                            (error.status === 401 && error.data && error.data.error !== 'expired_token') ||
                            (error.status === 401 && error.data && error.data.errors)) {

                            /* /me resource
                                1. any other error code different
                                2. expired_token condition
                                is triggered from LogHttpRESTAPIInterceptor
                                3. 401 error which is not handled by LogHttpRESTAPIInterceptor
                            */

                            const errorLocation = 'Auth.getMeAsync 401 Error';

                            ErrorHandler.handleError(error, errorLocation);

                            Auth.removeLoginCookies();

                            Auth.gotoLogin();

                        } else {

                            const errorLocation = 'Auth.getMeAsync Other Error';

                            // SSO reset password error (e.g. expired token)
                            if ($location.url().indexOf('auth/reset') > -1 &&
                                $location.url().indexOf('client_id=dashboard') > -1) {

                                Auth.removeLoginCookies();
                                Auth.gotoLogin();

                            }

                            // Handle all other errors
                            ErrorHandler.handleError(error, errorLocation);

                        }

                    });

            },

            /**
             * @ngdoc function
             * @name services.Auth#logOut
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Removes all hashtags from URL
             * Removes the cookies from session
             * Redirects to login page.
             */
            logOut: () => {

                const clearCookiesAndReload = () => {

                    // remove hashtags
                    $location.hash(null);

                    const acceptedRedirectUrl = 'https://support.avira.com/';

                    Auth.removeLoginTypeCookie();
                    Auth.removeLoginCookies();

                    Auth.removeSsoCookies();

                    const queryParams = $location.search();

                    // Set url if return_to param exists
                    if (queryParams.return_to && queryParams.return_to.indexOf('http') === 0) {

                        const unsafeCharacters = ['(', ')', '[', ']', '{', '}', '<', '>', '^', '|', 'javascript'];

                        const hasUnsafeChars = unsafeCharacters.find(char =>
                            queryParams.return_to.indexOf(char) > -1);

                        if (hasUnsafeChars ||
                            decodeURI(queryParams.return_to).indexOf(acceptedRedirectUrl) !== 0) {

                            $location.url('/auth/login');

                        } else {

                            $window.location.href = queryParams.return_to;

                        }

                        return true;

                    }

                    // Set SSO client_id to the new login flow
                    const loginPath = queryParams.client_id ? `/auth/login?client_id=${queryParams.client_id}` : '/auth/login';

                    $location.url(loginPath);

                    // wait for location path to be changed firefox/safari bug
                    $timeout(() => {

                        $window.location.reload();

                    }, 0);

                    return true;

                };

                $rootScope.State.loading = true;
                LogoutRestService.getAllAsync().then(() => {

                    clearCookiesAndReload();

                }).catch(() => {

                    clearCookiesAndReload();

                });

            },

            /**
             * @ngdoc function
             * @name services.Auth#loginWithFacebook
             * @methodOf services.Auth
             * @function
             *
             * @description
             * We make a http request (POST) with Facebook token
             * in order to log in the user into dashboard.
             *
             * @param {Object} payload Facebook authentication details
             */
            loginWithFacebook(payload, page) {

                const loginWithFacebookPayload = {
                    grant_type: 'fb_auth',
                    partner: $rootScope.appName,
                    token: payload.accessToken,
                    language: CWCONFIG.language,
                };

                // 2FA "remember me" header
                const oauthHeader = {
                    'X-Avira-Token': Auth.getTfaCookie(),
                };

                // add loading spinner until the Facebook login is ready
                Auth.socialLogin.socialLoginButton = 'facebook';
                Auth.socialLogin.loginAttempt = true;

                Auth.login(loginWithFacebookPayload, oauthHeader, page, 'Facebook');

            },

            /**
             * @ngdoc function
             * @name services.Auth#loginWithGooglePlus
             * @methodOf services.Auth
             * @function
             *
             * @description
             * We make a http request (POST) with Google token
             * in order to log in the user into dashboard.
             *
             * @param {Object} payload Google profile details
             */
            loginWithGooglePlus(payload, page) {

                const loginWithGooglePayload = {
                    grant_type: 'gg_auth',
                    partner: $rootScope.appName,
                    token: payload.access_token,
                };

                // 2FA "remember me" header
                const oauthHeader = {
                    'X-Avira-Token': Auth.getTfaCookie(),
                };

                // add loading spinner until the Google Plus login is ready
                Auth.socialLogin.socialLoginButton = 'google';
                Auth.socialLogin.loginAttempt = true;

                Auth.login(loginWithGooglePayload, oauthHeader, page, 'Google');

            },

            /**
             * @ngdoc function
             * @name services.Auth#loginWithAppleID
             * @methodOf services.Auth
             * @function
             *
             * @description
             * We make a http request (POST) with Apple token
             * in order to log in the user into dashboard.
             *
             * @param {Object} payload Apple profile details
             */
            loginWithAppleID(code, page) {

                const loginWithAppleIDPayload = {
                    grant_type: 'apl_auth',
                    partner: $rootScope.appName,
                    token: code,
                };

                // 2FA "remember me" header
                const oauthHeader = {
                    'X-Avira-Token': Auth.getTfaCookie(),
                };

                // add loading spinner until the Google Plus login is ready
                Auth.socialLogin.socialLoginButton = 'apple';
                Auth.socialLogin.loginAttempt = true;

                Auth.login(loginWithAppleIDPayload, oauthHeader, page, 'Apple');

            },

            /**
             * @ngdoc function
             * @name services.Auth#setLoginCookie
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Sets the session id and token cookies.
             */
            setLoginCookies(data) {

                let tokenExpireInDays;
                const grantType = Util.deepFind(data, 'config.data.grant_type');


                if (data && data.data && data.data.access_token) {

                    // the expire time is received in seconds and we convert it to days
                    tokenExpireInDays = parseInt(data.data.expires_in, 10) / 60 / 60 / 24;

                    Cookie.set(cookieConfig.token, data.data.access_token, {
                        expires: Math.round(tokenExpireInDays) || 3,
                        path: '/',
                        domain: rootDomain || domain,
                    });

                    Cookie.set(cookieConfig.refreshToken, data.data.refresh_token, {
                        expires: Math.round(tokenExpireInDays) || 3,
                        path: '/',
                        domain: rootDomain || domain,
                    });

                }

                // set cookie with user's login type (facebook, google, apple or password)
                if (grantType && (grantType === 'fb_auth' ||
                    grantType === 'gg_auth' ||
                    grantType === 'apl_auth' ||
                    grantType === 'password')) {

                    Cookie.set(cookieConfig.loginType, data.config.data.grant_type, {
                        expires: 90,
                        path: '/',
                        domain: rootDomain || domain,
                    });

                }

            },

            /**
             * @ngdoc function
             * @name services.Auth#setTfaCookie
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Sets the 2fa token cookie.
             */
            setTfaCookie(value) {

                Cookie.set(cookieConfig.tfat, value, {
                    path: '/',
                    domain,
                    expires: 90,
                });

            },

            /**
             * @ngdoc function
             * @name services.Auth#removeTfaCookie
             * @methodOf services.Auth
             * @function
             *
             * @description
             * removes the 2fa token cookie.
             */
            removeTfaCookie() {

                Cookie.remove(cookieConfig.tfat, {
                    path: '/',
                    domain,
                });

            },

            /**
             * @ngdoc function
             * @name services.Auth#getTfaCookie
             * @methodOf services.Auth
             * @function
             *
             * @description
             * gets the 2fa token cookie.
             */
            getTfaCookie() {

                return Cookie.get(cookieConfig.tfat);

            },

            /**
             * @ngdoc function
             * @name services.Auth#removeLoginCookies
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Remove all token cookies set at login.
             */
            removeLoginCookies() {

                Cookie.remove(cookieConfig.token, {
                    path: '/',
                    domain,
                });

                Cookie.remove(cookieConfig.refreshToken, {
                    path: '/',
                    domain,
                });

                Cookie.remove(cookieConfig.token, {
                    path: '/',
                    domain: rootDomain,
                });

                Cookie.remove(cookieConfig.refreshToken, {
                    path: '/',
                    domain: rootDomain,
                });

                setTimeout(() => {

                    MeRepository.replace();

                }, 0);

            },

            /**
             * @ngdoc function
             * @name services.Auth#removeLoginTypeCookie
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Remove login type cookie.
             */
            removeLoginTypeCookie() {

                Cookie.remove(cookieConfig.loginType, {
                    path: '/',
                    domain,
                });

            },

            /**
             * @ngdoc function
             * @name services.Auth#removeSsoCookies
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Remove all cookies set at login by the Sso service
             */
            removeSsoCookies() {

                // get all cookies
                const cookies = document.cookie;
                // regex to match all sso keys
                // example: prod_launcher_sso or prod_launcher_refresh_sso
                const regex = new RegExp(`^(${CWCONFIG.cookie.ssoToken}[a-z]+[_a-z]*?_sso)`, 'g');
                cookies.split('; ').map((cookie) => {

                    // check if cookie matches the regex
                    const match = cookie.match(regex);
                    if (match) {

                        Cookie.remove(match[0], {
                            path: '/',
                            domain,
                        });

                        Cookie.remove(match[0], {
                            path: '/',
                            domain: rootDomain,
                        });

                    }

                    return '';

                });

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoDashboard
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Redirect to dashboard page.
             */

            gotoDashboard() {

                $location.path('/dashboard');

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoLogin
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Clears the loading state and redirects to login page.
             */
            gotoLogin() {

                $rootScope.State.loading = false;
                $location.path('/auth/login');

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoSetPassword
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Clears the loading state and redirects to setPassword page.
             */
            gotoSetPassword() {

                const path = $location.url();

                const authExtraUrlParams = Session.getValue(SessionKeys.ExtraUrlParams);

                $rootScope.State.loading = false;

                if (authExtraUrlParams) {

                    const nextRoute = authExtraUrlParams.substring(authExtraUrlParams.indexOf('/dashboard'));

                    $location.url(`/auth/set-password${nextRoute}`);

                } else if (path.indexOf('subscriptions') > -1) {

                    $location.url(path);

                } else {

                    $location.url('/auth/set-password/default');

                }

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoRegister
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Clears the loading state and redirects to register page.
             */
            gotoRegister() {

                $rootScope.State.loading = false;
                $location.path('/auth/register');

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoConfirm
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Clears the loading state and redirects to confirmation page.
             */
            gotoConfirm() {

                $rootScope.State.loading = false;
                $location.path('/auth/confirm');

            },

            /**
             * @ngdoc function
             * @name services.Auth#gotoTfa
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Clears the loading state and redirects to TFA page.
             */
            gotoTfa() {

                $rootScope.State.loading = false;
                $location.path('/auth/tfa');

            },

            /**
             * @ngdoc function
             * @name services.Auth#startLogoutInterval
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Start an interval which logout current user based on the token expiration time
             */
            startLogoutInterval(tokenExpirationTime) {

                const tokenExpirationTimeInMiliseconds = tokenExpirationTime * 1000;
                /* substract one hour from token expiration time in order
                to logout the user before token expires */
                const logoutInterval = tokenExpirationTimeInMiliseconds - (3600 * 1000);

                $rootScope.logoutIntervalIsOn = true;

                if (logoutInterval && logoutInterval > 0) {

                    $timeout(() => {

                        Auth.logOut();

                    }, Math.min(logoutInterval, 2147483647));

                }

            },

            /**
             * @ngdoc function
             * @name services.Auth#logIntoAviraConnect
             * @methodOf services.Auth
             * @function
             *
             * @description
             * This method is used to log the user
             * into Avira Connect System (MYA/Launcher/Backend), not Dashboard
             */

            logIntoAviraConnect(userPayload, headers) {

                const loginPayload = {
                    grant_type: 'password',
                    username: userPayload.data.attributes.email,
                    password: userPayload.data.attributes.password,
                };

                const deviceToken = this.getDeviceToken();

                if (deviceToken) {

                    loginPayload.device_token = deviceToken;

                }

                if (!Util.isAnonymous()) {

                    headers.Authorization = `Basic ${Util.base64encode(CWCONFIG.oauthKey)}`;

                }

                /* this timeout is added because we have to wait 3-4 seconds
                   after a user is created to get a permanent token */
                $timeout(() => {

                    OAuthRestService
                        .post(loginPayload, headers)
                        .then((response) => {

                            Mixpanel.send('ConnectWeb Register Success', {
                                Method: 'password',
                            });

                            Auth.failedLogins = 0;
                            Auth.removeLoginCookies();
                            Auth.setLoginCookies(response);
                            Auth.setDeviceToken(response);
                            Auth.startLogoutInterval(response.data.expires_in);
                            Auth.getMeAsync();
                            Auth.sendAcpPing();

                            $rootScope.newRegisteredUser = false;

                        })
                        .then(null, (error) => {

                            const responseError = Util.deepFind(error, 'data.error');

                            Auth.failedLogins += 1;

                            Sentry.withScope((scope) => {

                                scope.setLevel('error');
                                Sentry.captureMessage(`User register error with code ${error.status}`, undefined, responseError);

                            });

                            switch (responseError) {

                            case 'required_captcha':
                                Recaptcha.enableCaptcha();
                                Auth.loading = false;
                                break;

                            case 'invalid_otp':
                                Mixpanel.send('ConnectWeb Login Error', {
                                    'Error Name': responseError,
                                    'Error Code': error.status,
                                });
                                break;

                            default:
                                Auth.loading = false;
                                break;

                            }

                            Alert.error('cw.notification.system.error');

                        });

                }, CWCONFIG.timeouts.authRegisterDelay);

            },

            /**
             * @ngdoc function
             * @name services.Auth#setDeviceToken
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Sets the device_token in local storage
             */
            setDeviceToken(data) {

                localStorage.setItem(CWCONFIG.localStorage.authDeviceToken, data.data.device_token);

            },

            /**
             * @ngdoc function
             * @name services.Auth#getDeviceToken
             * @methodOf services.Auth
             * @function
             *
             * @description
             * Get the device_token from local storage
             */

            getDeviceToken() {

                return localStorage.getItem(CWCONFIG.localStorage.authDeviceToken);

            },
        };

        /**
         * @ngdoc function
         * @name services.Auth#__triggerFormErrors
         * @methodOf services.Auth
         * @function
         *
         * @description
         * This method triggers alerts for common errors.
         */
        function __triggerFormErrors(error) {

            let responseError = false;

            if (error.data.errors && error.data.errors[0]) {

                responseError = error.data.errors[0].code;

            } else if (error.data.error) {

                responseError = error.data.error;

            }

            Auth.loading = false;

            switch (responseError) {

            case '21111': // email already exists

                Auth.error = true;
                Util.triggerAnimation('#error-message-form', 'animated shake');
                $rootScope.registerActiveStep = '1';

                break;

            case '909': // password not strong enough

                Alert.error('cw.validator.messages.password.strength');

                break;

            case '21109': // invalid user name

                Alert.error('cw.auth.error.full_name');

                break;

            case '918': // captcha required

                // show captcha directly
                Recaptcha.execute();

                break;
            case 'used_token':

                Alert.error('cw.notification.messages.reset_password_error');

                break;

            case '22906':

                $state.go('complianceFailed');

                break;

            default:

                Alert.error('cw.notification.system.error');

            }

        }

        return Auth;

    });



// WEBPACK FOOTER //
// ./scripts/common/services/Auth.js